import React, { useCallback, useEffect, useReducer, useRef } from 'react'
import { OrgNotificationProviderContext, orgNotificationsProviderReducer } from './internal'
import { useService } from '@pushly/aqe/lib/hooks'
import { ApiVersion } from '../../enums/api-version.enum'
import { CustomApiResponseErrorHandler } from '../../types/api-call-options-with-custom-error-handler'
import { AccountNotificationService } from 'src/services/account-notification'
import { simpleNotification } from '@pushly/aqe/lib/utils'
import { OrgNotificationModel } from '../../models/notification/org-notification.model'
import { parseNotificationVariantFromApiResponse } from '../../features/organizations/helpers/parse-org-notification-model'
import { arrayUnqiue } from '../../_utils/array'
import { InsightsService } from '../../services/insights'
import { InsightsReportContract } from '../../features/reporting_v2/helpers/build-api-report-contract'
import { ReportEntity } from '../../features/reporting_v2/enums/report-entity'
import { ReportDateIncrement } from '../../features/reporting_v2/enums/report-date-increment'
import { ReportDatePreset } from '../../features/reporting_v2/enums/report-date-preset'

type NotificationProviderProps = {
    orgId: number
    notificationId?: string | null
    notification?: OrgNotificationModel
    duplicateSourceId?: string | null
    onFetchError?: CustomApiResponseErrorHandler
    includeCancelledChildren?: boolean
    includeStats?: boolean
    children: React.ReactNode
}

export const OrgNotificationProvider = (props: NotificationProviderProps) => {
    const { orgId, includeCancelledChildren = false, includeStats = false, onFetchError, children } = props

    const propsNotificationId: string | undefined | null = props.notificationId
    const propsDuplicateSourceId: string | undefined | null = props.duplicateSourceId

    const orgNotifSvc = useService(AccountNotificationService)
    const statsSvc = useService(InsightsService)

    const [state, dispatch] = useReducer(orgNotificationsProviderReducer, {
        orgId,
        loading: !!propsNotificationId,
        notificationId: propsNotificationId ?? null,
        notification: null,
        duplicateSourceId: propsDuplicateSourceId ?? null,
    })

    // prevents duplicate load effects without triggering re-renders
    const localLoaderInitializedRef = useRef(false)

    const loadRemoteNotification = useCallback(async () => {
        const canLoadRemote = !localLoaderInitializedRef.current || orgId !== state.orgId
        if (!canLoadRemote) return

        if (!propsNotificationId) return

        if (!localLoaderInitializedRef.current) localLoaderInitializedRef.current = true
        if (!state.loading) dispatch({ type: 'loading:set', loading: true })

        // move to OrgNotificationsProvider
        const res = await orgNotifSvc.fetchById(orgId, propsNotificationId, {
            query: {
                include_segments: true,
            },
            version: ApiVersion.V4,
        })

        if (!res.ok && !res.cancelled && res.error) {
            simpleNotification('error', res.error.message)
            dispatch({ type: 'loading:set', loading: false })
        } else if (res.ok) {
            const data = res.data
            const notification = OrgNotificationModel.build(data)
            const variant = parseNotificationVariantFromApiResponse(data, includeCancelledChildren)

            const uqOrgSegmentIds = arrayUnqiue(notification.getSegmentIds() ?? [])

            if (uqOrgSegmentIds.length) {
                variant.getAudience().setSegmentIds(uqOrgSegmentIds)
            }

            notification.setVariant(variant)

            if (includeStats) {
                const domainIds = arrayUnqiue(notification.getNotifications().map((n) => n.domainId))

                const insightsPkg: InsightsReportContract = {
                    date_increment: ReportDateIncrement.LIFETIME,
                    date_preset: ReportDatePreset.LIFETIME,
                    fields: [
                        'notification_group.id',
                        'deliveries',
                        'impressions',
                        'clicks',
                        'ctr',
                        'ctr_decimal',
                        'delivery_rate',
                        'delivery_rate_decimal',
                    ],
                    entity: ReportEntity.NOTIFICATIONS,
                    breakdowns: ['notification_group'],
                    filters: [
                        {
                            field: 'domain.id',
                            operator: 'in',
                            value: domainIds,
                        },
                        {
                            field: 'notification_group.id',
                            operator: 'eq',
                            value: notification.getId(),
                        },
                    ],
                }

                const statsRes = await statsSvc.fetch(insightsPkg, {
                    showLoadingScreen: false,
                    cancellationKey: 'org-notifs-list-stats',
                    requestScope: 'organizations',
                    nestedScope: 'notifications',
                    scopeId: orgId,
                })
                const statsMatch = statsRes?.find((r) => r.notification_group?.id === notification.getId())

                if (!!statsMatch) {
                    notification.setStats({
                        deliveries: statsMatch.deliveries,
                        impressions: statsMatch.impressions,
                        clicks: statsMatch.clicks,
                        ctr: statsMatch.ctr,
                        ctr_decimal: statsMatch.ctr_decimal,
                        delivery_rate: statsMatch.delivery_rate,
                        delivery_rate_decimal: statsMatch.delivery_rate_decimal,
                    })
                }
            }

            dispatch({ type: 'notification:set', notification })
        }

        dispatch({ type: 'loading:set', loading: false })
    }, [orgId, propsNotificationId, includeCancelledChildren, includeStats, onFetchError])

    useEffect(() => {
        loadRemoteNotification()
    }, [loadRemoteNotification])

    return (
        <OrgNotificationProviderContext.Provider value={{ state, dispatch }}>
            {children}
        </OrgNotificationProviderContext.Provider>
    )
}
